import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, ArrowLeftIcon, UserIcon } from './Icons';
import { GoogleSheetsService } from '../services/GoogleSheetsService';

const StudentSearch = ({ onStudentSelected, onBack, sheetsService }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchQuery.trim()) {
      performSearch();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const performSearch = async () => {
    try {
      setLoading(true);
      const results = await sheetsService.searchStudents(searchQuery);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching students:', error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleStudentSelect = (student) => {
    // Automatically proceed to next screen when student is selected
    onStudentSelected(student);
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center py-4">
            <button
              onClick={onBack}
              className="mr-4 p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
            >
              <ArrowLeftIcon className="w-6 h-6 text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">New Student Signout</h1>
              <p className="text-gray-600 mt-1">Search and select a student</p>
            </div>
          </div>
        </div>
      </div>

      {/* Current Time Display */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="text-center">
            <div className="text-3xl font-mono text-gray-900">
              {formatTime(new Date())}
            </div>
            <div className="text-sm text-gray-500 mt-1">
              {formatDate(new Date())}
            </div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="mb-6">
            <div className="flex justify-between items-center mb-2">
              <label htmlFor="search" className="block text-sm font-medium text-gray-700">
                Search for Student
              </label>
                             {/* <button
                 onClick={async () => {
                   try {
                     setLoading(true);
                     
                     // First check token scopes
                     console.log('=== Checking Token Scopes ===');
                     const scopeInfo = sheetsService.checkTokenScopes();
                     
                     // Then test directory access
                     console.log('=== Testing Directory Access ===');
                     const hasAccess = await sheetsService.testDirectoryPermissions();
                     
                     if (hasAccess) {
                       alert('✅ Directory API access successful!\n\nCheck console for detailed scope and permission information.');
                     } else {
                       alert('❌ Directory API access failed.\n\nCheck console for detailed diagnostics and scope information.');
                     }
                   } catch (error) {
                     alert('❌ Error testing permissions: ' + error.message);
                   } finally {
                     setLoading(false);
                   }
                 }}
                 className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                 disabled={loading}
               >
                                   Test People API Access
               </button> */}
            </div>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                id="search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-primary focus:border-primary transition-colors duration-200"
                placeholder="Type student name to search..."
                autoFocus
              />
            </div>
          </div>

          {/* Search Results */}
          {loading ? (
            <div className="text-center py-8">
              <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-gray-500">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Searching...
              </div>
            </div>
          ) : searchResults.length > 0 ? (
            <div className="space-y-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Search Results</h3>
              {searchResults.map((student) => (
                <div
                  key={student.id}
                  onClick={() => handleStudentSelect(student)}
                  className="p-4 border border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:border-gray-300 hover:bg-gray-50"
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                        <UserIcon className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div className="ml-4 flex-1">
                      <div className="text-sm font-medium text-gray-900">
                        {student.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {student.email}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : searchQuery.trim() && !loading ? (
            <div className="text-center py-8">
              <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No students found</h3>
              <p className="mt-1 text-sm text-gray-500">
                Try searching with a different name or check the spelling.
              </p>
            </div>
          ) : null}


        </div>
      </div>
    </div>
  );
};

export default StudentSearch;
